import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { RunLogLine } from "~/api/types";
import { clockTime } from "~/features/runs/runDerive";
import { Empty } from "~/foundation/components/Empty";
import { cn } from "~/foundation/utils/cn";

/** Past this distance from the bottom the reader is reading, not following. Do not yank them. */
const STICK_THRESHOLD_PX = 24;

/**
 * The merged run log. Monospace and `dir="ltr"` on purpose: these lines are machine output —
 * node ids, paths, stack frames — and mirroring them in an RTL locale would reorder text the
 * server wrote left to right.
 */
export function RunLogsTab({ lines }: { lines: RunLogLine[] }) {
  const { t } = useTranslation();
  const paneRef = useRef<HTMLDivElement>(null);
  const stuck = useRef(true);

  // Follow the tail only while the reader is at the tail. Someone who scrolled up to read an
  // error four minutes back should not be dragged to the bottom every four seconds.
  const lineCount = lines.length;
  useEffect(() => {
    const pane = paneRef.current;
    if (pane && stuck.current && lineCount > 0) {
      pane.scrollTop = pane.scrollHeight;
    }
  }, [lineCount]);

  if (lines.length === 0) {
    return <Empty message={t("No log lines yet.")} />;
  }

  return (
    <div
      ref={paneRef}
      dir="ltr"
      onScroll={(event) => {
        const pane = event.currentTarget;
        stuck.current =
          pane.scrollHeight - pane.scrollTop - pane.clientHeight <= STICK_THRESHOLD_PX;
      }}
      className="max-h-96 overflow-y-auto rounded-md border border-divider bg-bg-secondary p-3 font-mono text-small"
    >
      {lines.map((line) => (
        <div
          key={line.id}
          className={cn(
            "whitespace-pre-wrap break-words",
            line.level === "error"
              ? "text-danger"
              : line.level === "warn"
                ? "text-warning"
                : "text-text-secondary",
          )}
        >
          <span className="text-text-tertiary">{clockTime(line.at)}</span>{" "}
          {line.nodeId ? <span className="text-text-tertiary">{line.nodeId}</span> : null}{" "}
          {line.message}
        </div>
      ))}
    </div>
  );
}
